// DTOs mirrored from Scout.Deals — meeting transcripts in project context, and the ledger read out of each one.

export interface TranscriptSummary {
  transcriptId: string
  project: string
  meeting: string
  date: string
  clipName: string
  speakers: string[]
  wordCount: number
  hasRecording: boolean
  meetingId: string | null
}

export interface TranscriptsPage {
  total: number
  projects: string[]
  transcripts: TranscriptSummary[]
}

export interface Transcript {
  transcriptId: string
  project: string
  meeting: string
  date: string
  clipName: string
  speakers: string[]
  text: string
  wordCount: number
  videoPath: string | null
  resourcesPath: string | null
  source: string
  meetingId: string | null
  importedAt: string
}

/* ── The ledger ── */

export type RequirementPriority = 'must' | 'should' | 'could'
export type RequirementStatus = 'proposed' | 'accepted' | 'declined'
export type TakeawayKind = 'ours' | 'theirs'
export type TakeawayStatus = 'open' | 'in-progress' | 'done' | 'dropped'
/** How a takeaway stands against its promised date, computed by the API against today. */
export type DueState = 'none' | 'ahead' | 'today' | 'overdue' | 'closed-on-time' | 'closed-late' | 'closed' | 'dropped'

export const REQUIREMENT_PRIORITIES: RequirementPriority[] = ['must', 'should', 'could']
export const REQUIREMENT_STATUSES: RequirementStatus[] = ['proposed', 'accepted', 'declined']
export const TAKEAWAY_STATUSES: TakeawayStatus[] = ['open', 'in-progress', 'done', 'dropped']

export interface TranscriptRequirement {
  requirementId: string
  transcriptId: string
  code: string
  /** EARS form: The <system> shall … / When <trigger>, the <system> shall … */
  text: string
  /** the speaker's own words, verbatim */
  quote: string
  timecode: string | null
  speaker: string | null
  priority: RequirementPriority
  status: RequirementStatus
  createdAt: string
  updatedAt: string
  editedAt: string | null
}

export interface Takeaway {
  takeawayId: string
  transcriptId: string
  kind: TakeawayKind
  text: string
  quote: string
  timecode: string | null
  speaker: string | null
  /** a user id when one of ours said it, else the speaker's name */
  promisedBy: string | null
  assigneeUserId: string | null
  /** the date said in the meeting */
  dueOn: string | null
  status: TakeawayStatus
  createdAt: string
  updatedAt: string
  closedAt: string | null
  editedAt: string | null
}

export interface TakeawayDue {
  state: DueState
  daysLate: number | null
  daysLeft: number | null
}

export interface TakeawayView {
  takeaway: Takeaway
  due: TakeawayDue
  assigneeName: string | null
  promisedByName: string | null
}

export interface AssigneeView {
  id: string
  displayName: string
}

export interface TranscriptAccount {
  meetingId: string
  companyId: string | null
  companyName: string | null
}

export interface LedgerTotals {
  transcripts: number
  extracted: number
  requirements: number
  openTakeaways: number
  overdue: number
  oursOwed: number
  theirsOwed: number
  unassigned: number
  dueThisWeek: number
}

export interface TranscriptLedgerCounts {
  transcriptId: string
  requirements: number
  takeaways: number
  open: number
  overdue: number
  oursOpen: number
  theirsOpen: number
  nextDue: string | null
  extractedAt: string | null
}

export interface LedgerOverview {
  totals: LedgerTotals
  transcripts: TranscriptLedgerCounts[]
  soonest: TakeawayView[]
  activeJobId: string | null
}

export interface TranscriptLedgerView {
  transcript: TranscriptSummary
  videoPath: string | null
  resourcesPath: string | null
  extractedAt: string | null
  extractedBy: string | null
  account: TranscriptAccount | null
  requirements: TranscriptRequirement[]
  takeaways: TakeawayView[]
  people: AssigneeView[]
  ourSpeakers: string[]
  activeJobId: string | null
  modelConfigured: boolean
}

export interface PatchTakeawayBody {
  status?: TakeawayStatus
  assigneeUserId?: string
  clearAssignee?: boolean
  dueOn?: string
  clearDue?: boolean
  text?: string
}

export interface PatchRequirementBody {
  status?: RequirementStatus
  priority?: RequirementPriority
  text?: string
}
